import React, { useEffect, useRef, useState } from 'react';
import { Builder_, Form, FormGroup, FormInput, FormLabel, Slider_, ValidatorError } from 'monica-alexandria';
import { useTranslation } from 'react-i18next';
import b from '../../services/api/Boards';
import { getCrmCheckoutUrl, goToCrmCheckout } from './crmCheckoutNavigation';

export default function CreateBoardPanel({ onCreated, onClose, lang = 'en' }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const close = () => { if (!pending.current) onClose(); };
  const create = async event => {
    event?.preventDefault();
    if (pending.current) return;
    const boardName = name.trim();
    if (!boardName || boardName.length > 120) {
      setError(t('Enter a board name of at most 120 characters'));
      return;
    }
    pending.current = true;
    setSaving(true);
    setError('');
    try {
      const result = await b.createBoard(boardName, lang);
      if (!mounted.current) return;
      const checkoutUrl = result?.status === 403 ? getCrmCheckoutUrl(result.data) : null;
      if (checkoutUrl) { goToCrmCheckout(checkoutUrl);return; }
      if (result?.data?.code === 'CRM_CREATION_ACCESS_UNAVAILABLE') {
        setError(t('Could not verify CRM access. Please try again.'));return;
      }
      if (!result || result.status >= 400 || !result.data?.board?._id) {
        setError(t(result?.data?.error || result?.data?.msg || 'Could not create board right now'));
        return;
      }
      onCreated(result.data.board);
    } catch {
      if (mounted.current) setError(t('Could not create board right now'));
    } finally {
      pending.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <Slider_
      disabled={saving}
      title={t('Create board')}
      text={saving ? t('Creating...') : t('Create board')}
      onClick={create}
      onClose={close}
      grey={t('Cancel')}
      onGrey={close}
    >
      <Form as="form" noPads onSubmit={create} aria-busy={saving} noValidate>
        <Builder_>
          <p>{t('Organize your contacts in a CRM board without an advertising quest.')}</p>
        </Builder_>
        <Builder_ last>
          <FormGroup>
            <FormInput
              id="create-crm-board-name"
              placeholder={t('Board name')}
              autoFocus
              autoComplete="off"
              maxLength={120}
              value={name}
              disabled={saving}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'create-crm-board-error' : undefined}
              onChange={event => { setName(event.target.value); setError(''); }}
            />
            <FormLabel htmlFor="create-crm-board-name">{t('Board name')}</FormLabel>
          </FormGroup>
          {error ? <ValidatorError id="create-crm-board-error" role="alert">{error}</ValidatorError> : null}
        </Builder_>
      </Form>
    </Slider_>
  );
}
